import { useState } from 'react';
import {
  getSupabaseConfigError,
  isSupabaseConfigured,
  supabase,
} from '../lib/supabase';

const VEHICLE_OPTIONS = ['Any', 'Sedan', 'SUV', 'Hatchback'];

const initialForm = {
  customer_name: '',
  customer_phone: '',
  pickup_address: '',
  dropoff_address: '',
  pickup_city: '',
  vehicle_type: 'Any',
  scheduled_at: '',
  notes: '',
};

function normalizePhone(value) {
  const digits = String(value || '').replace(/\D/g, '');
  if (digits.length === 12 && digits.startsWith('91')) return digits.slice(2);
  if (digits.length === 11 && digits.startsWith('0')) return digits.slice(1);
  return digits;
}

function BookTaxi({ embedded = false }) {
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });

  const onChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const next = {};
    if (!form.customer_name.trim()) next.customer_name = 'Name is required.';
    const phone = normalizePhone(form.customer_phone);
    if (!/^[6-9]\d{9}$/.test(phone)) {
      next.customer_phone = 'Enter a valid 10-digit mobile number.';
    }
    if (!form.pickup_address.trim()) next.pickup_address = 'Pickup is required.';
    if (!form.dropoff_address.trim()) {
      next.dropoff_address = 'Drop-off is required.';
    }
    return next;
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    setStatus({ type: '', message: '' });

    const configError = getSupabaseConfigError();
    if (configError || !isSupabaseConfigured || !supabase) {
      setStatus({
        type: 'error',
        message: configError || 'Booking is unavailable.',
      });
      return;
    }

    const nextErrors = validate();
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setSubmitting(true);
    const payload = {
      customer_name: form.customer_name.trim(),
      customer_phone: normalizePhone(form.customer_phone),
      pickup_address: form.pickup_address.trim(),
      dropoff_address: form.dropoff_address.trim(),
      pickup_city: form.pickup_city.trim() || null,
      vehicle_type: form.vehicle_type || 'Any',
      scheduled_at: form.scheduled_at
        ? new Date(form.scheduled_at).toISOString()
        : null,
      notes: form.notes.trim() || null,
      status: 'pending',
    };

    const { error } = await supabase.from('rides').insert(payload);
    setSubmitting(false);

    if (error) {
      const message = (error.message || '').toLowerCase();
      setStatus({
        type: 'error',
        message:
          message.includes('rides') || error.code === '42P01'
            ? 'Ride booking is not set up yet. Run supabase/rides.sql in the SQL Editor.'
            : error.message || 'Could not submit your ride request.',
      });
      return;
    }

    setForm(initialForm);
    setStatus({
      type: 'success',
      message:
        'Ride request submitted. Our team will assign a driver and call you shortly.',
    });
  };

  const formBlock = (
        <form className="book-taxi__form" onSubmit={onSubmit} noValidate>
          <div className="field-row">
            <label className={`field ${errors.customer_name ? 'field--error' : ''}`}>
              <span>Your name</span>
              <input
                name="customer_name"
                value={form.customer_name}
                onChange={onChange}
                placeholder="Full name"
                autoComplete="name"
              />
              {errors.customer_name && <em>{errors.customer_name}</em>}
            </label>
            <label className={`field ${errors.customer_phone ? 'field--error' : ''}`}>
              <span>Mobile number</span>
              <input
                name="customer_phone"
                value={form.customer_phone}
                onChange={onChange}
                placeholder="10-digit mobile"
                inputMode="numeric"
                autoComplete="tel"
              />
              {errors.customer_phone && <em>{errors.customer_phone}</em>}
            </label>
          </div>

          <label className={`field ${errors.pickup_address ? 'field--error' : ''}`}>
            <span>Pickup address</span>
            <input
              name="pickup_address"
              value={form.pickup_address}
              onChange={onChange}
              placeholder="Street, landmark, area"
              autoComplete="street-address"
            />
            {errors.pickup_address && <em>{errors.pickup_address}</em>}
          </label>

          <label className={`field ${errors.dropoff_address ? 'field--error' : ''}`}>
            <span>Drop-off address</span>
            <input
              name="dropoff_address"
              value={form.dropoff_address}
              onChange={onChange}
              placeholder="Destination"
            />
            {errors.dropoff_address && <em>{errors.dropoff_address}</em>}
          </label>

          <div className="field-row">
            <label className="field">
              <span>City</span>
              <input
                name="pickup_city"
                value={form.pickup_city}
                onChange={onChange}
                placeholder="City"
              />
            </label>
            <label className="field">
              <span>Vehicle</span>
              <select
                name="vehicle_type"
                value={form.vehicle_type}
                onChange={onChange}
              >
                {VEHICLE_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="field-row">
            <label className="field">
              <span>When (optional)</span>
              <input
                type="datetime-local"
                name="scheduled_at"
                value={form.scheduled_at}
                onChange={onChange}
              />
            </label>
            <label className="field">
              <span>Notes (optional)</span>
              <input
                name="notes"
                value={form.notes}
                onChange={onChange}
                placeholder="Airport, luggage, etc."
              />
            </label>
          </div>

          {status.message && (
            <div className={`form-alert form-alert--${status.type}`} role="status">
              {status.message}
            </div>
          )}

          <button type="submit" className="btn btn--primary" disabled={submitting}>
            {submitting ? 'Submitting…' : 'Submit hire-a-car request'}
          </button>
        </form>
  );

  if (embedded) {
    return (
      <div className="book-taxi book-taxi--embedded">
        <header className="hire-car-panel__head">
          <h3>Trip details</h3>
          <p>We will call you to confirm fare and driver assignment.</p>
        </header>
        {formBlock}
      </div>
    );
  }

  return (
    <section id="book" className="book-taxi section">
      <div className="section__inner book-taxi__inner">
        <div className="section__head">
          <p className="eyebrow">Hire a car</p>
          <h2>Plan your outstation or scheduled trip.</h2>
          <p className="section__lead">
            Submit a request and Saarthi will assign an approved driver from our fleet.
          </p>
        </div>
        {formBlock}
      </div>
    </section>
  );
}

export default BookTaxi;
